import { atomWithKey as atom } from "./atomWithKey";
import { ILegTrain } from "../Types/Index";
import {
    IRideEstimate,
    IRideSearchState,
    RideHailingMode,
    RideResultsStatus,
} from "../Types/Interface/IRideHailing";
import {
    CarRentalField,
    CarRentalResultsStatus,
    ICarRentalBranch,
    ICarRentalSearchState,
    ICarRentalV2Segment,
} from "../Types/Interface/ICarRental";
import {
    IShuttleAddedJourney,
    IShuttleAddRequest,
    IShuttleSelection,
    IShuttleStopOption,
    IShuttleTrip,
    ShuttleOffersStatus,
} from "../Types/Interface/IShuttleBus";
import { INolSelection, INolAdded, INolProduct } from "../Types/Interface/INol";

/** Legs of the trip whose "Manage End-to-end Journey" row was clicked. */
export const endToEndSelectedTripAtom = atom<ILegTrain[] | null>({
    key: "endToEndSelectedTripAtom",
    default: null,
});

/** toStation: destination prefilled = departure station; fromStation: pickup prefilled = arrival station. */
export const rideHailingModeAtom = atom<RideHailingMode>({
    key: "rideHailingModeAtom",
    default: "toStation",
});

export const rideSearchStateAtom = atom<IRideSearchState>({
    key: "rideSearchStateAtom",
    default: { when: "now", pickup: null, destination: null, pickupDate: null, pickupTime: null },
});

export const rideEstimatesAtom = atom<IRideEstimate[] | null>({
    key: "rideEstimatesAtom",
    default: null,
});

export const rideResultsStatusAtom = atom<RideResultsStatus>({
    key: "rideResultsStatusAtom",
    default: "idle",
});

/**
 * Latched for the session when beef answers 404 on a ride-hailing route, which
 * means RIDE_HAILING_ENABLED=false server-side. useRideHailingEnabled() folds
 * this in so every entry point disappears instead of offering a panel that
 * cannot work.
 */
export const rideHailingDisabledAtom = atom<boolean>({
    key: "rideHailingDisabledAtom",
    default: false,
});

export const rideLocationPickerAtom = atom<{ open: boolean; field: "pickup" | "destination" }>({
    key: "rideLocationPickerAtom",
    default: { open: false, field: "pickup" },
});

/** toStation: return prefilled = departure station; fromStation: pickup prefilled = arrival station. */
export const carRentalModeAtom = atom<RideHailingMode>({
    key: "carRentalModeAtom",
    default: "toStation",
});

export const carRentalSearchStateAtom = atom<ICarRentalSearchState>({
    key: "carRentalSearchStateAtom",
    default: {
        pickupBranch: null,
        returnBranch: null,
        pickupDate: null,
        pickupTime: null,
        returnDate: null,
        returnTime: null,
    },
});

/** Thrifty rental branches (flattened across emirates) for the search dropdowns. */
export const carRentalLocationsAtom = atom<ICarRentalBranch[]>({
    key: "carRentalLocationsAtom",
    default: [],
});

export const carRentalResultsStatusAtom = atom<CarRentalResultsStatus>({
    key: "carRentalResultsStatusAtom",
    default: "idle",
});

/** V2 rate-card-v2 vehicle segments (denominations + images) for the current search. */
export const carRentalV2SegmentsAtom = atom<ICarRentalV2Segment[] | null>({
    key: "carRentalV2SegmentsAtom",
    default: null,
});

/** Which side ("toStation"/"fromStation") the shuttle flow is operating on. */
export const shuttleBusModeAtom = atom<RideHailingMode>({
    key: "shuttleBusModeAtom",
    default: "toStation",
});

/** Live selection for the open side: stop, time/offer, riders, edit context. */
export const shuttleSelectionAtom = atom<IShuttleSelection>({
    key: "shuttleSelectionAtom",
    default: {
        side: "toStation",
        stop: null,
        trip: null,
        selectedPassengerRefs: [],
        isEdit: false,
    },
});

/** SHT stop options for the open side's station. */
export const shuttleStopsAtom = atom<IShuttleStopOption[]>({
    key: "shuttleStopsAtom",
    default: [],
});

/** Shuttle trips for the currently-selected stop. */
export const shuttleTripsAtom = atom<IShuttleTrip[] | null>({
    key: "shuttleTripsAtom",
    default: null,
});

export const shuttleOffersStatusAtom = atom<ShuttleOffersStatus>({
    key: "shuttleOffersStatusAtom",
    default: "idle",
});

/** Lazy session cache of the "from Đx" min per-pax price, keyed by `${mode}:${stationId}`. */
export const shuttleMinPriceCacheAtom = atom<Record<string, number>>({
    key: "shuttleMinPriceCacheAtom",
    default: {},
});

/**
 * Which shuttle stops actually have departures, keyed by
 * `${mode}:${stationId}:${stopId}:${departureDate}`. Written by the offers sweep
 * (warmShuttleStopPrices) so surfaces can drop a stop that sells nothing —
 * Abu Dhabi lists both Corniche and ADNEC as shuttle sub-stations, but only the
 * ones with trips belong in the picker.
 *
 * `false` means the sweep produced no trips for that stop — whether the backend
 * answered with an empty list OR the request failed. Both are treated the same
 * on purpose: either way there is nothing to sell, and leaving the row visible
 * only leads to a modal that can say "please try again later" and nothing else
 * (Fujairah → Main Bus Station, where /offers 4xx's).
 *
 * A stop that has not been swept yet is ABSENT, and absence reads as "unknown,
 * keep showing it" — the picker starts complete and narrows once answers land,
 * never the reverse.
 *
 * A failure can only ever hide a stop that has NEVER answered: warmShuttleStopPrices
 * preserves an existing `true` rather than overwriting it. That matters because
 * there is no automatic recovery path once a stop is hidden — the preload effect
 * does not re-run (its deps are stable after load) and the modal's sweep is
 * unreachable once the row is gone.
 *
 * Keyed WITH the travel date: shuttle service is per-day and this atom is never
 * reset, so a dateless key would let one day's "no departures" hide the stop on
 * every later date.
 */
export const shuttleStopAvailabilityAtom = atom<Record<string, boolean>>({
    key: "shuttleStopAvailabilityAtom",
    default: {},
});

/**
 * Shuttle offers THIS session has refunded, by offerId.
 *
 * A refund settles through the payment session, so for a few seconds the offer
 * is still on the booking with non-terminal admissions — indistinguishable in
 * the payload from a live one (`createdOn` is the BOOKING's timestamp, identical
 * on every offer, so recency cannot separate them either). In that window a
 * refund followed straight by a re-book showed the REFUNDED shuttle as the
 * current one until a page reload (reported 2026-07-27).
 *
 * The client is the only party that knows, so it remembers. Entries are inert
 * once the booking reports the offer terminal — that filter drops it anyway — so
 * a leftover id is harmless; hydration also clears the list when the same hook
 * instance moves to a different booking (a remount does not, which is fine:
 * offer ids are UUIDs and cannot match another booking's offers).
 *
 * In-memory by design: after a reload the server's view is authoritative, and a
 * persisted id could outlive the refund it describes.
 */
export const refundedShuttleOfferIdsAtom = atom<string[]>({
    key: "refundedShuttleOfferIdsAtom",
    default: [],
});

/**
 * A pending "add a shuttle" request raised by Manage booking — the details-page
 * menu, or the bookings-list card which routes to details with `?addShuttle=1`.
 *
 * Identity only. ManageEndToEndRow decides what to do with it from live data:
 * open the section, expand the sides that can still take a shuttle, and open the
 * modal only when the booking leaves exactly one place for it. Null when idle.
 */
export const shuttleAddRequestAtom = atom<IShuttleAddRequest | null>({
    key: "shuttleAddRequestAtom",
    default: null,
});

/** Confirmed shuttle journeys attached to the booking, keyed by side. */
export const shuttleAddedAtom = atom<Partial<Record<RideHailingMode, IShuttleAddedJourney>>>({
    key: "shuttleAddedAtom",
    default: {},
});

/** Which side ("toStation"/"fromStation") the NOL flow is operating on. */
export const nolCardModeAtom = atom<RideHailingMode>({
    key: "nolCardModeAtom",
    default: "toStation",
});

/** Live NOL selection for the open side. */
export const nolSelectionAtom = atom<INolSelection>({
    key: "nolSelectionAtom",
    default: { productCode: null, passengerRefs: [], isEdit: false },
});

/**
 * Purchasable NOL variants keyed by leg id, from GET /bookings/{id}/fmlm-offers.
 *
 * Previously NOL was read out of mealPreferencesResponseAtom (the ADDONS
 * payload) and filtered by product code. It has its own source now — the addons
 * atom is for real addons only.
 */
export const nolOffersAtom = atom<Record<string, INolProduct[]>>({
    key: "nolOffersAtom",
    default: {},
});

/** Confirmed NOL summaries attached to the booking, keyed by side. */
export const nolAddedAtom = atom<Partial<Record<RideHailingMode, INolAdded>>>({
    key: "nolAddedAtom",
    default: {},
});
